import React from "react";
import { useNavigate } from "react-router-dom";
import { Bot, Sparkles, BarChart3, Search, Wrench, MoonStar } from "lucide-react";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import GuestChatBox from "@/components/guru-chat/GuestChatBox";

/**
 * Tela pública (teaser) do Guru Chat para visitantes/deslogados.
 * Não envia mensagens — apresenta o recurso e direciona para login/cadastro.
 */
const GuruPublicTeaser: React.FC = () => {
  const navigate = useNavigate();

  const examples = [
    { icon: MoonStar, label: "Interpretar sonho", text: "Sonhei com cobra, qual o palpite?" },
    { icon: Sparkles, label: "Palpite do dia", text: "Qual o melhor palpite para hoje?" },
    { icon: BarChart3, label: "Ver tendência", text: "Qual a tendência forte do dia?" },
    { icon: Search, label: "Buscar bicho", text: "Está saindo muito qual bicho?" },
    { icon: Wrench, label: "Melhor ferramenta agora", text: "Qual ferramenta usar agora?" },
  ];

  return (
    <>
      <SEOHead
        title="Guru Chat — Converse com a IA do Palpite Guru"
        description="Conheça o Guru Chat: interprete sonhos, peça palpites do dia e descubra a melhor ferramenta para o jogo do bicho."
        canonical="/guru-chat"
      />
      <div className="container mx-auto px-4 py-8 max-w-3xl">
        <div className="text-center space-y-4">
          <div className="inline-flex h-20 w-20 items-center justify-center rounded-2xl bg-gradient-to-br from-purple-600 via-amber-500 to-yellow-500 shadow-2xl shadow-amber-500/30">
            <Bot className="h-10 w-10 text-zinc-900" strokeWidth={2.4} />
          </div>
          <h1 className="text-3xl md:text-4xl font-extrabold">
            Conheça <span className="bg-gradient-to-r from-amber-500 to-yellow-500 bg-clip-text text-transparent">O Guru</span>
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Seu assistente inteligente do IA Palpite Guru. Interprete sonhos, peça palpites do dia,
            descubra tendências e saiba qual ferramenta usar agora.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-8">
          {examples.map((ex) => {
            const Icon = ex.icon;
            return (
              <div
                key={ex.label}
                className="rounded-xl border border-amber-500/20 bg-gradient-to-br from-amber-500/5 to-yellow-500/5 p-4 flex items-start gap-3"
              >
                <div className="rounded-lg bg-amber-500/15 p-2 shrink-0">
                  <Icon className="h-5 w-5 text-amber-500" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-bold text-foreground">{ex.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5 break-words">"{ex.text}"</p>
                </div>
              </div>
            );
          })}
        </div>

        <GuestChatBox />

        <div className="mt-8 rounded-2xl border border-amber-500/30 bg-gradient-to-br from-zinc-900 to-zinc-800 p-6 text-center space-y-4 shadow-xl">
          <h2 className="text-xl font-bold text-amber-300">Entre para conversar com o Guru</h2>
          <p className="text-sm text-zinc-300">
            Cadastre-se grátis e fale com a IA do Palpite Guru.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center">
            <Button
              size="lg"
              className="bg-gradient-to-r from-amber-500 to-yellow-500 text-zinc-900 font-bold hover:from-amber-600 hover:to-yellow-600"
              onClick={() => navigate("/login")}
            >
              Entrar
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
              onClick={() => navigate("/cadastro")}
            >
              Criar conta grátis
            </Button>
          </div>
        </div>

        <p className="text-[11px] text-muted-foreground text-center mt-6">
          📊 Plataforma baseada em análises estatísticas. Sem garantia de premiação.
        </p>
      </div>
    </>
  );
};

export default GuruPublicTeaser;
